// Zine artwork lives in site/images/ as <stem>.{avif,webp,png}.
// Add new pages by encoding images/1.png/2.png/... and re-running scripts/build-images.sh,
// then list the stem in the spread/page tables below.
const ASSETS = {
  img1: 'images/1',
  img2: 'images/2',
  img3: 'images/3',
  img4: 'images/4',
  img5: 'images/5',
  img6: 'images/6',
  img7: 'images/7',
};

const { useState, useEffect, useLayoutEffect, useRef, useCallback } = React;

/** Must match `zineFlipNext` / `zineFlipPrev` duration in zine.css */
const FLIP_MS = 750;
/** Must match the `.zine-mobile__slide` transition in zine.css */
const MOBILE_SLIDE_MS = 320;
/** Min horizontal pixels for a swipe to count as a page-turn */
const SWIPE_THRESHOLD_PX = 40;

// Pages: each spread = [left stem, right stem].
// null stem = render the booking CTA in that slot. 7 unique pages plus the
// cover (stem '1') reused as page 8, bookended by the CTA on both ends.
const PAGES = [
  { left: null,         right: ASSETS.img1, leftLabel: 'CTA',             rightLabel: '01 Cover' },
  { left: ASSETS.img2,  right: ASSETS.img3, leftLabel: '02 Page 2',       rightLabel: '03 Page 3' },
  { left: ASSETS.img4,  right: ASSETS.img5, leftLabel: '04 Page 4',       rightLabel: '05 Page 5' },
  { left: ASSETS.img6,  right: ASSETS.img7, leftLabel: '06 Page 6',       rightLabel: '07 Page 7' },
  { left: ASSETS.img1,  right: null,        leftLabel: '08 Cover (back)', rightLabel: 'CTA' },
];

// Flat list of individual pages for mobile in straight reading order
// (booking CTA is rendered below the zine, not as a swipe).
const MOBILE_PAGES = [
  { stem: ASSETS.img1, label: '01 Cover' },
  { stem: ASSETS.img2, label: '02 Page 2' },
  { stem: ASSETS.img3, label: '03 Page 3' },
  { stem: ASSETS.img4, label: '04 Page 4' },
  { stem: ASSETS.img5, label: '05 Page 5' },
  { stem: ASSETS.img6, label: '06 Page 6' },
  { stem: ASSETS.img7, label: '07 Back Cover' },
];

const TOTAL_SPREADS = PAGES.length;
const ZINE_STEMS = Object.freeze(Object.values(ASSETS));

// Same-origin endpoint backed by the kit-subscribe Lambda (see
// infra/lambda_subscribe.tf + infra/lambda/kit-subscribe/index.mjs).
// CloudFront forwards /api/* straight to the Function URL, so the browser
// sees a plain same-origin POST: no CORS preflight, no third-party load.
const SUBSCRIBE_URL = '/api/subscribe';

/** Merged into `payload.utm`; Lambda maps these to Kit subscriber `fields` — create matching custom fields in Kit (same keys). */
const SIGNUP_UTM_KEYS = Object.freeze([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
]);

const SIGNUP_UTM_STORAGE_KEY = 'im_signup_utm';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── helpers ───────────────────────────────────────────────────────────────

/** Persist campaign params across in-site navigation before the visitor subscribes. */
function persistSignupUtmFromLocation() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
  try {
    let prev = {};
    try {
      prev = JSON.parse(sessionStorage.getItem(SIGNUP_UTM_STORAGE_KEY) || '{}');
    } catch (_) {
      prev = {};
    }
    const sp = new URLSearchParams(window.location.search);
    const next = { ...prev };
    for (const key of SIGNUP_UTM_KEYS) {
      const v = sp.get(key);
      if (v != null && String(v).trim()) next[key] = String(v).trim();
    }
    if (Object.keys(next).length > 0) {
      sessionStorage.setItem(SIGNUP_UTM_STORAGE_KEY, JSON.stringify(next));
    }
  } catch (_) {
    /* ignore quota / privacy mode */
  }
}

/** Current URL overrides session (e.g. user opened a new campaign link in the same tab). */
function mergeSignupUtmForSubmit() {
  if (typeof window === 'undefined') return {};
  let stored = {};
  try {
    stored = JSON.parse(sessionStorage.getItem(SIGNUP_UTM_STORAGE_KEY) || '{}');
  } catch (_) {
    stored = {};
  }
  const fresh = {};
  const sp = new URLSearchParams(window.location.search);
  for (const key of SIGNUP_UTM_KEYS) {
    const v = sp.get(key);
    if (v != null && String(v).trim()) fresh[key] = String(v).trim();
  }
  return { ...stored, ...fresh };
}

/** True if the event target is (or is inside) something that should keep its own click behaviour. */
function isInteractiveTarget(target) {
  if (!target || typeof target.closest !== 'function') return false;
  return Boolean(
    target.closest('a, button, input, select, textarea, label, [role="button"]')
  );
}

// 1×1 px test data URIs used to feature-detect AVIF / WebP support before
// preloading. If we picked the wrong format the browser would still recover
// (the `<picture>` source list also covers it), but feature-detecting first
// means our preload waterfall fetches the same bytes the browser will use.
const AVIF_TEST_DATA_URI =
  'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK';
const WEBP_TEST_DATA_URI =
  'data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==';

function canDecode(dataUri) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img.width > 0 && img.height > 0);
    img.onerror = () => resolve(false);
    img.src = dataUri;
  });
}

async function detectBestImageExt() {
  const [avifOk, webpOk] = await Promise.all([
    canDecode(AVIF_TEST_DATA_URI),
    canDecode(WEBP_TEST_DATA_URI),
  ]);
  if (avifOk) return 'avif';
  if (webpOk) return 'webp';
  return 'png';
}

function preloadOne(src) {
  return new Promise((resolve) => {
    const img = new Image();
    const finish = () => resolve();
    img.onload = () => {
      if (typeof img.decode === 'function') {
        img.decode().then(finish).catch(finish);
      } else {
        finish();
      }
    };
    img.onerror = finish;
    img.src = src;
  });
}

async function preloadZineImages() {
  const ext = await detectBestImageExt();
  await Promise.all(ZINE_STEMS.map((stem) => preloadOne(`${stem}.${ext}`)));
}

// ─── hooks ─────────────────────────────────────────────────────────────────

/** After a touch swipe navigates, browsers may fire a synthetic click — skip the next page-face click. */
function useSuppressClickAfterTouchNav() {
  const ref = useRef(false);
  const mark = useCallback(() => {
    ref.current = true;
    window.setTimeout(() => {
      ref.current = false;
    }, 450);
  }, []);
  return [ref, mark];
}

/** Tracks "is this viewport portrait-ish?" — true on phones and narrow windows. */
function usePortrait() {
  const compute = () =>
    window.innerWidth < window.innerHeight || window.innerWidth < 600;
  const [portrait, setPortrait] = useState(compute);
  useEffect(() => {
    const onResize = () => setPortrait(compute());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return portrait;
}

function isArrowNavBlockedTarget(target) {
  if (!(target instanceof Element)) return false;
  if (target.closest('input, textarea, select, [contenteditable="true"]'))
    return true;
  return false;
}

/** Bind ←/→ arrow keys to navigation callbacks. */
function useArrowKeyNav(onPrev, onNext) {
  useEffect(() => {
    const handler = (e) => {
      if (isArrowNavBlockedTarget(e.target)) return;
      if (e.key === 'ArrowRight') onNext();
      else if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onPrev, onNext]);
}

/**
 * Detect a horizontal swipe on `window` and call `onSwipe(direction)` where
 * direction is -1 (swipe right → previous) or +1 (swipe left → next).
 * Vertical drags are ignored.
 */
function useHorizontalSwipe(onSwipe) {
  useEffect(() => {
    let startX = null;
    let startY = null;
    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      startX = null;
      startY = null;
      if (Math.abs(dx) > SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy) * 1.5) {
        onSwipe(dx < 0 ? 1 : -1);
      }
    };
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [onSwipe]);
}

// ─── components ────────────────────────────────────────────────────────────

/**
 * <picture> wrapper for a zine page. Always advertises avif → webp → png so
 * the browser picks the best supported format. The PNG fallback is also the
 * one used by social-image unfurlers (OG meta references images/1.png).
 */
function ZineImage({ stem, alt, className }) {
  return (
    <picture>
      <source srcSet={`${stem}.avif`} type="image/avif" />
      <source srcSet={`${stem}.webp`} type="image/webp" />
      <img
        className={className}
        src={`${stem}.png`}
        alt={alt}
        draggable={false}
        loading="eager"
        decoding="async"
      />
    </picture>
  );
}

function MailingListSignup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [message, setMessage] = useState('');

  const locked = status === 'submitting' || status === 'success';

  const clearErrorOnEdit = useCallback(() => {
    if (status === 'error') {
      setStatus('idle');
      setMessage('');
    }
  }, [status]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (locked) return;
      const emailValue = email.trim();
      const nameValue = name.trim();
      if (!EMAIL_RE.test(emailValue)) {
        setStatus('error');
        setMessage('that email doesn’t look right — try again?');
        return;
      }

      setStatus('submitting');
      setMessage('');

      // The Lambda accepts `email`, optional `name` (sent as Kit `first_name`),
      // and optional `utm` (SIGNUP_UTM_KEYS → subscriber `fields` in Kit). On any Kit-side failure the Lambda still
      // returns 200 ok and logs a `kit_subscribe_failed` event so the
      // signup can be reconciled by hand (see scripts/list-failed-signups.sh).
      // Treat any non-2xx as our own bug, not a Kit outage.
      const payload = { email: emailValue };
      if (nameValue) payload.name = nameValue;
      const utm = mergeSignupUtmForSubmit();
      if (Object.keys(utm).length > 0) payload.utm = utm;

      try {
        const res = await fetch(SUBSCRIBE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`subscribe responded ${res.status}`);
        setStatus('success');
        setMessage('');
        setName('');
        setEmail('');
      } catch (err) {
        console.warn('[mailing-list] subscribe failed', err);
        setStatus('error');
        setMessage('hmm, that didn’t go through — try again later');
      }
    },
    [name, email, locked]
  );

  const buttonLabel =
    status === 'submitting' ? 'sending…'
    : status === 'success' ? 'subscribed'
    : 'subscribe';

  return (
    <form className="mailing-list" onSubmit={onSubmit} noValidate>
      <div className="mailing-list__title">newsletter</div>
      <p className="mailing-list__lede">
        sign up for updates on when and where I'll be cutting hair, zine releases and other relevant info
      </p>
      <input
        type="text"
        className="mailing-list__input"
        name="name"
        placeholder="name (optional)"
        value={name}
        onChange={(ev) => { setName(ev.target.value); clearErrorOnEdit(); }}
        autoComplete="given-name"
        aria-label="Name (optional)"
        disabled={locked}
      />
      <input
        type="email"
        className="mailing-list__input"
        name="email"
        placeholder="email"
        value={email}
        onChange={(ev) => { setEmail(ev.target.value); clearErrorOnEdit(); }}
        autoComplete="email"
        aria-label="Email address"
        disabled={locked}
        required
      />
      <button
        type="submit"
        className="mailing-list__btn"
        disabled={locked}
      >
        {buttonLabel}
      </button>
      {message && (
        <div
          className={`mailing-list__msg mailing-list__msg--${status}`}
          role={status === 'error' ? 'alert' : 'status'}
        >
          {message}
        </div>
      )}
    </form>
  );
}

function BookingPage() {
  return (
    <div className="booking-page">
      <a
        className="booking-title"
        href="https://book.squareup.com/appointments/pqlbz8tga7u7pm/location/L8PRKJDRN9JE6/services"
        target="_blank"
        rel="noopener noreferrer"
      >
        book a cut
      </a>

      <a
        className="booking-btn"
        href="mailto:tristan@interesting-monsters.com"
        target="_blank"
        rel="noopener noreferrer"
      >
        contact me
      </a>

      <MailingListSignup />

      <div className="booking-info">
        Cambridge, UK<br/>
        instagram: <a href="https://www.instagram.com/interesting_monsters">@interesting_monsters</a><br/>
        tristan (they/he)
      </div>
    </div>
  );
}

function ZinePage({ stem, label }) {
  if (!stem) {
    if (label === 'CTA') return <BookingPage />;
    // Fallback placeholder for unexpected null stems (mid-flip transition).
    return (
      <div className="zine-page-placeholder">
        <svg viewBox="0 0 60 60">
          <rect x="10" y="10" width="40" height="40" fill="none" stroke="#fff" strokeWidth="1"/>
          <line x1="10" y1="30" x2="50" y2="30" stroke="#fff" strokeWidth="0.5"/>
          <line x1="30" y1="10" x2="30" y2="50" stroke="#fff" strokeWidth="0.5"/>
        </svg>
      </div>
    );
  }
  return <ZineImage stem={stem} alt={label} />;
}

function FlipLeaf({ direction, running, frontStem, frontLabel, backStem, backLabel }) {
  return (
    <div className={`flip-panel flip-leaf--${direction}${running ? ' flip-run' : ''}`}>
      <div className="flip-face front">
        <ZinePage stem={frontStem} label={frontLabel} />
      </div>
      <div className="flip-face back">
        <ZinePage stem={backStem} label={backLabel} />
      </div>
    </div>
  );
}

function NavDots({ count, current, onSelect, label }) {
  return (
    <nav className="nav-dots" aria-label={label}>
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className={`nav-dot${i === current ? ' active' : ''}`}
          onClick={() => onSelect(i)}
          role="button"
          tabIndex={0}
          aria-label={`Go to ${i + 1}`}
          onKeyDown={(e) => e.key === 'Enter' && onSelect(i)}
        />
      ))}
    </nav>
  );
}

function EdgeArrow({ side, onClick, label }) {
  const points = side === 'prev' ? '15 18 9 12 15 6' : '9 6 15 12 9 18';
  return (
    <button className={`edge-arrow ${side}`} onClick={onClick} aria-label={label}>
      <svg viewBox="0 0 24 24"><polyline points={points}/></svg>
    </button>
  );
}

function ZineMobile() {
  const [page, setPage] = useState(0);
  const [sliding, setSliding] = useState(false);
  const [slideDir, setSlideDir] = useState(null);
  const [displayed, setDisplayed] = useState(0);
  const total = MOBILE_PAGES.length;
  const timeout = useRef(null);
  const [suppressPageClick, markTouchNavigation] = useSuppressClickAfterTouchNav();

  const goTo = useCallback((target) => {
    if (sliding || target === page || target < 0 || target >= total) return;
    setSlideDir(target > page ? 'left' : 'right');
    setSliding(true);
    timeout.current = setTimeout(() => {
      setDisplayed(target);
      setPage(target);
      setSliding(false);
      setSlideDir(null);
    }, MOBILE_SLIDE_MS);
  }, [sliding, page, total]);

  useArrowKeyNav(
    useCallback(() => goTo(page - 1), [goTo, page]),
    useCallback(() => goTo(page + 1), [goTo, page]),
  );

  // Portrait: swipe either direction; tapping the page also goes forward.
  useHorizontalSwipe(useCallback((dir) => {
    markTouchNavigation();
    goTo(page + dir);
  }, [page, goTo, markTouchNavigation]));

  useEffect(() => () => clearTimeout(timeout.current), []);

  const current = MOBILE_PAGES[displayed];
  const isLast = page >= total - 1;

  const onMobilePageClick = useCallback(
    (e) => {
      if (sliding || suppressPageClick.current) return;
      if (isInteractiveTarget(e.target)) return;
      if (isLast) return;
      goTo(page + 1);
    },
    [sliding, page, isLast, goTo, suppressPageClick]
  );

  const slideStyle = {
    transition: sliding
      ? `transform ${MOBILE_SLIDE_MS}ms cubic-bezier(0.4,0,0.2,1), opacity ${MOBILE_SLIDE_MS}ms`
      : 'none',
    transform: sliding
      ? (slideDir === 'left' ? 'translateX(-100%)' : 'translateX(100%)')
      : 'translateX(0)',
    opacity: sliding ? 0 : 1,
  };

  return (
    <div className="zine-mobile">
      <div className="zine-mobile__viewport">
        <div className="zine-mobile__frame">
          <div
            className={`zine-mobile__slide${isLast ? ' zine-mobile__slide--last' : ''}`}
            onClick={onMobilePageClick}
            style={slideStyle}
          >
            <ZineImage
              className="zine-mobile__media"
              stem={current.stem}
              alt={current.label}
            />
          </div>
        </div>
        {page > 0 && !sliding && (
          <EdgeArrow side="prev" onClick={() => goTo(page - 1)} label="Previous page" />
        )}
        {!isLast && !sliding && (
          <EdgeArrow side="next" onClick={() => goTo(page + 1)} label="Next page" />
        )}
        <NavDots count={total} current={page} onSelect={goTo} label="Page navigation" />
        <div className="page-counter">{page + 1} / {total}</div>
        <div className="scroll-hint" aria-hidden="true">
          scroll down to book a cut
          <span className="scroll-hint__arrow">↓</span>
        </div>
        <div className="zine-label">interesting monsters</div>
      </div>
      <section className="zine-mobile__cta" aria-label="Book a cut">
        <BookingPage />
      </section>
    </div>
  );
}

function ZineBook() {
  const [spread, setSpread] = useState(0);
  const [flipping, setFlipping] = useState(false);
  const [direction, setDirection] = useState(null); // 'next' | 'prev' | null
  const [pendingSpread, setPendingSpread] = useState(null);
  const [isFlipActive, setIsFlipActive] = useState(false);
  /** Deferred so the leaf paints at 0° before the keyframe animation starts */
  const [flipRunKeyframes, setFlipRunKeyframes] = useState(false);

  const flipEndTimer = useRef(null);
  const flipRafs = useRef({ a: null, b: null });
  const [suppressPageClick, markTouchNavigation] = useSuppressClickAfterTouchNav();

  const clearFlipTimer = useCallback(() => {
    if (flipEndTimer.current != null) {
      clearTimeout(flipEndTimer.current);
      flipEndTimer.current = null;
    }
  }, []);

  useLayoutEffect(() => {
    const cancelFlipRafs = () => {
      if (flipRafs.current.a != null) cancelAnimationFrame(flipRafs.current.a);
      if (flipRafs.current.b != null) cancelAnimationFrame(flipRafs.current.b);
      flipRafs.current = { a: null, b: null };
    };

    if (!isFlipActive) {
      cancelFlipRafs();
      setFlipRunKeyframes(false);
      return;
    }

    setFlipRunKeyframes(false);
    flipRafs.current.a = requestAnimationFrame(() => {
      flipRafs.current.a = null;
      flipRafs.current.b = requestAnimationFrame(() => {
        flipRafs.current.b = null;
        setFlipRunKeyframes(true);
      });
    });
    return cancelFlipRafs;
  }, [isFlipActive, direction, pendingSpread]);

  const goTo = useCallback((targetSpread) => {
    if (flipping || targetSpread === spread) return;
    if (targetSpread < 0 || targetSpread >= TOTAL_SPREADS) return;

    clearFlipTimer();
    setDirection(targetSpread > spread ? 'next' : 'prev');
    setPendingSpread(targetSpread);
    setFlipping(true);
    setIsFlipActive(true);

    flipEndTimer.current = setTimeout(() => {
      flipEndTimer.current = null;
      setSpread(targetSpread);
      setFlipping(false);
      setIsFlipActive(false);
      setPendingSpread(null);
    }, FLIP_MS);
  }, [flipping, spread, clearFlipTimer]);

  useArrowKeyNav(
    useCallback(() => goTo(spread - 1), [goTo, spread]),
    useCallback(() => goTo(spread + 1), [goTo, spread]),
  );

  useHorizontalSwipe(useCallback((dir) => {
    markTouchNavigation();
    goTo(spread + dir);
  }, [spread, goTo, markTouchNavigation]));

  useEffect(() => () => clearFlipTimer(), [clearFlipTimer]);

  const current = PAGES[spread];
  const target = pendingSpread !== null ? PAGES[pendingSpread] : null;
  const isNext = direction === 'next';
  const isPrev = direction === 'prev';

  // Real-book leaf textures: front = currently-visible side of the turning page,
  // back = the new page that will land on the opposite half when the leaf finishes.
  const flipFrontStem = isNext ? current?.right : current?.left;
  const flipFrontLabel = isNext ? current?.rightLabel : current?.leftLabel;
  const flipBackStem = isNext ? target?.left : target?.right;
  const flipBackLabel = isNext ? target?.leftLabel : target?.rightLabel;

  // Static halves under the leaf — set once at flip start, no mid-flip swap needed.
  // Forward: left stays current.left; right shows next.right (covered by leaf during 0–90°).
  // Backward: right stays current.right; left shows prev.left (covered by leaf at start).
  // Use `target != null ? …` not `??` so an intentional null stem (booking slot) stays null.
  const staticLeft =
    isNext ? current.left : target != null ? target.left : current.left;
  const staticRight =
    isPrev ? current.right : target != null ? target.right : current.right;
  const staticLeftLabel =
    isNext ? current.leftLabel : target != null ? target.leftLabel : current.leftLabel;
  const staticRightLabel =
    isPrev ? current.rightLabel : target != null ? target.rightLabel : current.rightLabel;

  const handleLeftPageClick = useCallback(
    (e) => {
      if (flipping || suppressPageClick.current) return;
      if (isInteractiveTarget(e.target)) return;
      goTo(spread - 1);
    },
    [flipping, spread, goTo, suppressPageClick]
  );

  const handleRightPageClick = useCallback(
    (e) => {
      if (flipping || suppressPageClick.current) return;
      if (isInteractiveTarget(e.target)) return;
      goTo(spread + 1);
    },
    [flipping, spread, goTo, suppressPageClick]
  );

  return (
    <>
      <div className="zine-label">interesting monsters</div>

      <div className="zine-stage">
        <div className={`zine-book${isFlipActive ? ' zine-book--flipping' : ''}`}>

          {/* LEFT HALF — backward leaf hinges here */}
          <div className="page-half left">
            <div className="page-face zine-page-face--turn" onClick={handleLeftPageClick}>
              <ZinePage
                stem={isFlipActive ? staticLeft : current.left}
                label={isFlipActive ? staticLeftLabel : current.leftLabel}
              />
            </div>

            {isFlipActive && isPrev && (
              <FlipLeaf
                key={`flip-prev-${spread}-${pendingSpread ?? spread}`}
                direction="prev"
                running={flipRunKeyframes}
                frontStem={flipFrontStem}
                frontLabel={flipFrontLabel}
                backStem={flipBackStem}
                backLabel={flipBackLabel}
              />
            )}
          </div>

          {/* RIGHT HALF — forward leaf hinges here */}
          <div className="page-half right">
            <div className="page-face zine-page-face--turn" onClick={handleRightPageClick}>
              <ZinePage
                stem={isFlipActive ? staticRight : current.right}
                label={isFlipActive ? staticRightLabel : current.rightLabel}
              />
            </div>

            {isFlipActive && isNext && (
              <FlipLeaf
                key={`flip-next-${spread}-${pendingSpread ?? spread}`}
                direction="next"
                running={flipRunKeyframes}
                frontStem={flipFrontStem}
                frontLabel={flipFrontLabel}
                backStem={flipBackStem}
                backLabel={flipBackLabel}
              />
            )}
          </div>

          {spread > 0 && !flipping && (
            <EdgeArrow side="prev" onClick={() => goTo(spread - 1)} label="Previous spread" />
          )}
          {spread < TOTAL_SPREADS - 1 && !flipping && (
            <EdgeArrow side="next" onClick={() => goTo(spread + 1)} label="Next spread" />
          )}
        </div>
      </div>

      <NavDots count={TOTAL_SPREADS} current={spread} onSelect={goTo} label="Spread navigation" />

      <div className="page-counter">{spread + 1} / {TOTAL_SPREADS}</div>
    </>
  );
}

function App() {
  const portrait = usePortrait();
  const [imagesReady, setImagesReady] = useState(false);

  useEffect(() => {
    persistSignupUtmFromLocation();
  }, []);

  useEffect(() => {
    let cancelled = false;
    preloadZineImages().then(() => {
      if (!cancelled) setImagesReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  if (!imagesReady) {
    return <div className="zine-preload-root">loading</div>;
  }

  return portrait ? <ZineMobile /> : <ZineBook />;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
